/**
 * Pairs management page.
 * Tabbed list (List / Matching). Pairs are created from sessions — no create button.
 */

import * as React from 'react';
import { X } from 'lucide-react';
import { useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import { ManagementPage } from '@/components/common/ManagementPage';
import { PairsDataTable } from '@/components/pairs/remote/PairsDataTable';
import { DissociationModal } from '@/components/pairs/remote/DissociationModal';
import { ManagerEditModal } from '@/components/pairs/remote/ManagerEditModal';
import { CreateSessionView } from '@/components/sessions/remote/CreateSessionView';
import { Button } from '@/components/forms/Button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { usePair, useExportPairs } from '@/hooks/usePairs';
import { usePageActions } from '@/hooks/usePageActions';
import { usePermissions } from '@/hooks/usePermissions';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { useViewMode } from '@/hooks/useViewMode';
import { useDetailsView } from '@/hooks/useDetailsView';
import { canAddOneOffSession } from '@/components/pairs/remote/pair-table-utils';
import type { PairRecord, PairQueryParams } from '@/types/remote/pair.types';
import { ConfigureMatchingView } from '@/components/pairs/remote/matching/ConfigureMatchingView';
import {
  PAIRS_TAB_COLORS,
  PAIRS_TAB_ICONS,
  PAIRS_TAB_LABELS,
  type PairsPageTab,
} from '@/components/pairs/pair-configs';
import type { TabItem } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';

/** Deep-link: filter the list by tutor or student passed via `location.state`. */
interface PairPersonFilter {
  tutorUserId?: string;
  studentUserId?: string;
  personName?: string;
}

/** Read a tutor / student pin from router state. */
function readPairPersonFilter(state: unknown): PairPersonFilter {
  if (!state || typeof state !== 'object') return {};
  const payload = state as PairPersonFilter;
  return {
    tutorUserId: payload.tutorUserId,
    studentUserId: payload.studentUserId,
    personName: payload.personName,
  };
}

/** Remote pairs list; create/edit tutor forms replace the table. */
export function PairsManagementPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const pageActions = usePageActions('pairs');
  const { canCreate: canCreateSession, creatableTypes: sessionCreatableTypes } =
    usePageActions('sessions');
  /** Same create-session right as tutoring (existing backoffice permission). */
  const canAddOneOffFromPair =
    canCreateSession &&
    (sessionCreatableTypes.length === 0 || sessionCreatableTypes.includes('tutoring'));
  const accessibleTabs = React.useMemo<PairsPageTab[]>(
    () => (pageActions.canCreate ? ['list', 'matching'] : ['list']),
    [pageActions.canCreate],
  );
  const tabItems = React.useMemo<TabItem<PairsPageTab>[]>(() => {
    const items: TabItem<PairsPageTab>[] = [];
    for (const id of accessibleTabs) {
      const Icon = PAIRS_TAB_ICONS[id];
      items.push({
        id,
        label: PAIRS_TAB_LABELS[id],
        icon: <Icon className="h-4 w-4" />,
        color: PAIRS_TAB_COLORS[id],
      });
    }
    return items;
  }, [accessibleTabs]);

  const initialTab = (location.state as { initialTab?: PairsPageTab } | null)?.initialTab ?? 'list';
  /** Restore the last tab on browser back (`POP`); otherwise use location.state or list. */
  const [activeTab, setActiveTab] = React.useState<PairsPageTab>(() => {
    if (navigationType === 'POP') {
      const saved = sessionStorage.getItem('dt-tab:pairs-remote') as PairsPageTab | null;
      if (saved) return saved;
    }
    return initialTab;
  });
  React.useEffect(() => {
    sessionStorage.setItem('dt-tab:pairs-remote', activeTab);
  }, [activeTab]);

  React.useEffect(() => {
    if (!accessibleTabs.includes(activeTab)) {
      setActiveTab(accessibleTabs[0]);
    }
  }, [accessibleTabs, activeTab]);

  const [dissociatingPairId, setDissociatingPairId] = React.useState<string | null>(null);
  const [editingManagerPairId, setEditingManagerPairId] = React.useState<string | null>(null);
  const [oneOffSessionPair, setOneOffSessionPair] = React.useState<PairRecord | null>(null);
  const [activeApiFilters, setActiveApiFilters] = React.useState<PairQueryParams>({});
  const [personFilter, setPersonFilter] = React.useState<PairPersonFilter>(() =>
    readPairPersonFilter(location.state),
  );

  React.useEffect(() => {
    const next = readPairPersonFilter(location.state);
    if (!next.tutorUserId && !next.studentUserId) return;
    setPersonFilter(next);
    setActiveTab('list');
    void navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const { viewMode, setViewMode } = useViewMode('pairs-management');
  const { openRowId, openRow, setOpenRow, closeRow } = useDetailsView();
  const [searchParams] = useSearchParams();

  /** Deep-link: expand a pair row via `?view=` (keep the param so remount still opens). */
  React.useEffect(() => {
    const viewId = searchParams.get('view');
    if (viewId) {
      setActiveTab('list');
      setOpenRow(viewId);
    }
  }, [searchParams, setOpenRow]);

  useInterfaceElements('sessions');
  const { canViewInterface } = usePermissions();
  const canViewSessions = canViewInterface('sessions');

  const { data: dissociatingPair } = usePair(dissociatingPairId);
  const { data: editingManagerPair } = usePair(editingManagerPairId);
  const exportMutation = useExportPairs();

  function handleTabChange(tabId: string): void {
    setActiveTab(tabId as PairsPageTab);
    closeRow();
  }

  function handleCloseDissociation(open: boolean): void {
    if (!open) setDissociatingPairId(null);
  }

  function handleDissociationSuccess(): void {
    setDissociatingPairId(null);
    closeRow();
  }

  function handleCloseManagerEdit(open: boolean): void {
    if (!open) setEditingManagerPairId(null);
  }

  function handleManagerEditSuccess(): void {
    setEditingManagerPairId(null);
  }

  function handleEditPair(id: string): void {
    setEditingManagerPairId(id);
  }

  function handleDissociatePair(id: string): void {
    setDissociatingPairId(id);
  }

  /** Open the one-off session form for an active pair only. */
  function handleAddOneOffSession(pair: PairRecord): void {
    if (!canAddOneOffSession(pair)) return;
    setOneOffSessionPair(pair);
  }

  function handleCloseOneOffSession(): void {
    setOneOffSessionPair(null);
  }

  function handleOneOffSessionSuccess(): void {
    setOneOffSessionPair(null);
  }

  function handleToggleRow(id: string): void {
    if (openRowId === id) {
      closeRow();
      return;
    }
    openRow(id);
  }

  function handleClearPersonFilter(): void {
    setPersonFilter({
      tutorUserId: undefined,
      studentUserId: undefined,
      personName: undefined,
    });
  }

  function renderPersonFilterBanner(): React.ReactNode {
    if (!personFilter.personName) return null;
    return (
      <div className="mb-4 p-3 rounded-lg bg-[color-mix(in_srgb,var(--color-2-nuance-1)_10%,transparent)] border border-[var(--color-2-nuance-1)] flex items-center justify-between">
        <span className="text-sm font-medium" style={{ color: 'var(--color-2-nuance-1)' }}>
          Binômes de {personFilter.personName}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearPersonFilter}
          tooltipText="Effacer le filtre"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  /** Jump to sessions filtered by this pair's tutor + student. */
  const handleViewSessions = React.useCallback(
    (pair: PairRecord) => {
      void navigate('/sessions', {
        state: {
          tutorUserId: pair.tutorUserId,
          studentUserId: pair.studentUserId,
          personName: `${pair.tutorName} ↔ ${pair.studentName}`,
        },
      });
    },
    [navigate],
  );

  /** Download the current filtered list as xlsx (blob URL is revoked after click). */
  const handleExportExcel = async () => {
    try {
      const blob = await exportMutation.mutateAsync(activeApiFilters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `binomes-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  function renderListTab(): React.ReactNode {
    if (activeTab !== 'list') return null;
    return (
      <>
        {renderPersonFilterBanner()}
        <PairsDataTable
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          expandedRowId={openRowId}
          onToggleRow={handleToggleRow}
          onCloseDetails={closeRow}
          onEditPair={pageActions.canEditRow ? handleEditPair : undefined}
          onDissociatePair={pageActions.canDissociate ? handleDissociatePair : undefined}
          onViewSessions={canViewSessions ? handleViewSessions : undefined}
          onAddOneOffSession={canAddOneOffFromPair ? handleAddOneOffSession : undefined}
          onFiltersChange={setActiveApiFilters}
          pinnedTutorUserId={personFilter.tutorUserId}
          pinnedStudentUserId={personFilter.studentUserId}
          pageActions={{
            canEditRow: pageActions.canEditRow,
            canExportExcel: pageActions.canExportExcel,
            canDissociate: pageActions.canDissociate,
            canViewSessions,
            canAddOneOffSession: canAddOneOffFromPair,
          }}
          onExportExcel={pageActions.canExportExcel ? handleExportExcel : undefined}
        />
      </>
    );
  }

  function renderMatchingTab(): React.ReactNode {
    if (activeTab !== 'matching') return null;
    return <ConfigureMatchingView />;
  }

  return (
    <>
      <ManagementPage
        title="Gestion des Binômes"
        subtitle="Vue d'ensemble des binômes de tutorat distanciel"
        tabs={{
          items: tabItems,
          activeTab,
          onTabChange: handleTabChange,
        }}
      >
        {renderListTab()}
        {renderMatchingTab()}
      </ManagementPage>

      <DissociationModal
        open={!!dissociatingPairId}
        onOpenChange={handleCloseDissociation}
        pair={dissociatingPair ?? null}
        onSuccess={handleDissociationSuccess}
      />

      <ManagerEditModal
        open={!!editingManagerPairId}
        onOpenChange={handleCloseManagerEdit}
        pair={editingManagerPair ?? null}
        onSuccess={handleManagerEditSuccess}
      />

      <Dialog
        open={!!oneOffSessionPair}
        onOpenChange={(open) => {
          if (!open) handleCloseOneOffSession();
        }}
      >
        <DialogContent
          className="max-w-lg h-auto max-h-[90vh] p-0 custom-scroll-area"
          hideCloseButton
        >
          {oneOffSessionPair && (
            <CreateSessionView
              sessionType="tutoring"
              onClose={handleCloseOneOffSession}
              onSuccess={handleOneOffSessionSuccess}
              pairId={oneOffSessionPair.id}
              initialTutorId={oneOffSessionPair.tutorUserId}
              initialTutorLabel={oneOffSessionPair.tutorName}
              initialStudentId={oneOffSessionPair.studentUserId}
              initialStudentLabel={oneOffSessionPair.studentName}
              initialManagedById={oneOffSessionPair.managedBy?.userId}
              initialManagedByLabel={oneOffSessionPair.managedBy?.fullName}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
